import type { Ref } from 'vue';
import { ref } from 'vue';
import { useTopDialog } from './utils';
import { waitWhile } from '@/core/utils/system';
import type { AsyncTopDialogComponentLoader, TopDialogComponent } from './types';
import { TopDialogHandle } from './dialogHandle';

/**
 * Объект для работы с асинхронным диалоговым окном
 *
 * Является прослойкой для доступа к TopDialogHandle
 */
export class AsyncTopDialogHandle<T extends TopDialogComponent> {

	/**
	 * Функция, определяющая компонент TopDialogComponent
	 */
	readonly resolve: AsyncTopDialogComponentLoader<T>;

	/**
	 * Реактивная Сссылка на объект worker,
	 *
	 * Ссылка не будет указывать на объект, если диалоговое окно еще не загружено
	 */
	readonly worker: Ref<TopDialogHandle<T> | null> = ref(null);

	constructor(resolve: AsyncTopDialogComponentLoader<T>) {
		this.resolve = resolve;
	}

	/**
	 * Получить доступ к TopDialogHandle
	 */
	async get() {
		if (this.worker.value) return this.worker.value;

		const result = await this.resolve();
		const TopDialogComponent = result.default;

		this.worker.value = useTopDialog(TopDialogComponent);

		const worker = this.worker.value;

		await waitWhile(() => !worker.isMounted);

		return worker;
	}

	/**
	 * Открыть диалоговое окно
	 */
	async open(...args: Parameters<TopDialogHandle<T>['open']>) {
		const worker = await this.get();

		await worker.open(...args);
	}

	/**
	 * Открыть диалоговое окно как ссылку, для хеш-навигации
	 *
	 * Только общедоступные диалоговые окна, расположенные в @/dialogs
	 */
	async openAsLink(...args: Parameters<TopDialogHandle<T>['open']>) {
		const worker = await this.get();

		void worker.openAsLink(...args);
	}

	/**
	 * Закрыть диалоговое окно
	 *
	 * Не меняет состояние компонента диалогового окна
	 */
	async close(...args: Parameters<TopDialogHandle<T>['close']>) {
		const worker = await this.get();

		worker.close(...args);
	}

	/**
	 * Размонтировать виджет диалогового окна
	 */
	async destroy(...args: Parameters<TopDialogHandle<T>['close']>) {
		const worker = await this.get();

		worker.destroy(...args);
	}

	/**
	 * Устанвоить props
	 *
	 * Полностью сбрасывается состояние props
	 */
	async setProps(...args: Parameters<TopDialogHandle<T>['setProps']>) {
		const worker = await this.get();

		await worker.setProps(...args);
	}
}
